import { useState, useEffect, useRef } from 'react'
import {
  Box,
  Checkbox,
  FormLabel,
  FormControlLabel,
  FormControl,
  FormGroup,
  Modal,
  Slider,
  Button,
  Divider,
} from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'
import { device } from '../breakpoints'
import { MarkerData } from 'Types/markers'
import styled, { useTheme } from 'styled-components'
import locales from 'locales/locales'

const Wrapper = styled.div`
  color: ${(props) => props.theme.textColor};

  // TODO
  .MuiSlider-markLabel {
    color: white !important;
  }
`

const FilterContainer = styled.div`
  width: 40%;
  min-width: 250px;
`

export const Filters = ({
  markers,
  handleFilter,
  currentSearch,
}: {
  markers: MarkerData[]
  handleFilter: (filteredMarkers: MarkerData[]) => void
  currentSearch: string
}) => {
  const [filters, setFilters] = useState(new Map())
  const [showFilters, setShowFilters] = useState(false)
  const filterButtonRef = useRef<HTMLButtonElement>(null)
  const theme = useTheme()

  const maxPrice = markers.reduce((max: number, marker: MarkerData) => {
    const price = marker.filters.find((f) => f.name === 'Prix')
    const priceValue = price?.value as number
    return price && priceValue > max ? priceValue : max
  }, 0)

  useEffect(() => {
    const counts = new Map()
    // Count the number of restaurants for each filter
    markers.forEach((marker) => {
      marker.filters.forEach((filter) => {
        let { name, value } = filter
        if (!counts.has(name)) counts.set(name, new Map())
        const valueCounts = counts.get(name)
        if (valueCounts.has(value)) {
          valueCounts.set(value, {
            ...valueCounts.get(value),
            count: valueCounts.get(value).count + 1,
          })
        } else {
          valueCounts.set(value, { active: false, count: 1, name: value })
        }
      })
    })

    setFilters(counts)
  }, [markers])

  useEffect(() => {
    let filteredMarkers = markers

    // Find only the relevant markers, according to the filters
    filteredMarkers = markers.filter((m) => {
      let keep = true

      filters.forEach((value, key) => {
        if (keep) {
          const markerValues = m.filters
            .filter((f) => f.name === key)
            .map((f) => f.value)

          const multiValue = markerValues.find((value) =>
            value.toString().includes(' / ')
          )
          if (multiValue) {
            console.log('Multi!', multiValue, multiValue.toString().split(' / '))
            // console.log(multiValue, typeof multiValue, multiValue.split(' / '))
            markerValues.push(...multiValue.toString().split(' / '))
          }

          const filterValues = [...value.values()]
            .filter((v) => v.active)
            .map((v) => v.name)

          keep = markerValues.some((mv) => filterValues.includes(mv))

          // If no filter is active in this category, show the marker
          if (filterValues.length === 0) keep = true
        }
      })
      return keep
    })

    // Find only the relevant markers, according to the search box
    filteredMarkers = filteredMarkers.filter((m) => {
      if (currentSearch !== '') {
        return m.text
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .includes(
            currentSearch
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .toLowerCase()
          )
      } else {
        return true
      }
    })

    handleFilter(filteredMarkers)
  }, [filters, currentSearch])

  function compareFilters(a: any, b: any) {
    if (a.name < b.name) {
      return -1
    }
    if (a.name > b.name) {
      return 1
    }
    return 0
  }
  const ouvert = filters.get('Ouvert aujourd’hui')
    ? [...filters.get('Ouvert aujourd’hui').values()]
      .filter((f) => !f.name.includes(' / '))
      .sort(compareFilters)
    : []
  
  const categories = filters.get('Catégorie')
    ? [...filters.get('Catégorie').values()].sort(compareFilters)
    : []


  const cuisine = filters.get('Cuisine')
    ? [...filters.get('Cuisine').values()]
      .filter((f) => (!f.name.includes(' / ') && (f.name !== '')))
      .sort(compareFilters)
    : []

  const prix =
    filters.get('Prix')
      ? [...filters.get('Prix').values()]
        .filter((v: any) => v.active)
        .map((f: any) => f.name)
        .sort()
      : [1, 4]

  const handleFilterClick = (categorie: string, filter: any) => {
    const newFilters = new Map(filters)
    newFilters
      .get(categorie)
      .set(filter.name, { ...filter, active: !filter.active })
    setFilters(newFilters)
  }

  return (
    <Wrapper>
      <Button
        ref={filterButtonRef}
        color='buttonDark'
        disableElevation
        startIcon={<TuneIcon />}
        variant='contained'
        onClick={() => {
          setShowFilters(!showFilters)
          if (filterButtonRef.current) {
            const rect = filterButtonRef.current.getBoundingClientRect()
            // Quick and dirty, should handle the modal modification on the dom and then scroll
            setTimeout(() => { filterButtonRef.current && filterButtonRef.current.scrollIntoView() }, 100)
          }
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            event: 'Interactions',
            event_action: 'click',
            event_label: `showfilters`,
          })
        }}
      >
        {locales.buttonFilters[theme.lang]}
      </Button>
      <Modal
        open={showFilters}
        onClose={() => {
          setShowFilters(false)
        }}
      >
        <Window>
          {/* <Header>
            <div>Filtrer les établissements</div>
            <CloseButton
            onClick={() => {
              setShowFilters(false)
            }}
          />
          </Header> */}

          <MenuContainer>
            <MenuInfos>{locales.filtersTitle[theme.lang]}</MenuInfos>
            <CloseButton
              onClick={() => {
                setShowFilters(false)
              }}
            >
              <Close />
            </CloseButton>
          </MenuContainer>

          <Content>
            {['tdg', '24heures'].includes(theme.publisherDomain) && <Box mb={2} pt={2}>
              <FormControl>
                <FormLabel sx={{ color: 'inherit' }} id='is-open'>
                  {locales.openToday[theme.lang]}
                </FormLabel>
                <p style={{ fontSize: '0.8em' }}>
                  <i>{locales.openNote[theme.lang]}</i>
                </p>

                <FormGroup>
                  {ouvert.map((c) => (
                    <FormControlLabel
                      key={c.name}
                      control={
                        <Checkbox
                          sx={{
                            '& .MuiSvgIcon-root': { fontSize: 30 },
                            color: 'inherit',
                          }}
                          onChange={() => {
                            handleFilterClick('Ouvert aujourd’hui', c)
                          }}
                          checked={c.active}
                        />
                      }
                      label={c.name}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Box>}
            {['bernerzeitung', 'derbund'].includes(theme.publisherDomain) && <Box mb={2} pt={2}>
              <FormControl>
                <FormLabel sx={{ color: 'inherit' }} id='restaurant-type'>
                  {locales.regionTypeTitle[theme.lang]}
                </FormLabel>
                <FormGroup>
                  {categories.map((c) => (
                    <FormControlLabel
                      key={c.name}
                      control={
                        <Checkbox
                          sx={{
                            '& .MuiSvgIcon-root': { fontSize: 30 },
                            color: 'inherit',
                          }}
                          onChange={() => {
                            handleFilterClick('Catégorie', c)
                          }}
                          checked={c.active}
                        />
                      }
                      label={
                        c.name
                      }
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Box>}

            <Divider />

            {theme.lang === 'fr' && <Box mb={2} pt={2}>
              <FormControl>
                <FormLabel sx={{ color: 'inherit' }} id='restaurant-type'>
                  {locales.restaurantTypeTitle[theme.lang]}
                </FormLabel>
                <FormGroup>
                  {categories.map((c) => (
                    <FormControlLabel
                      key={c.name}
                      control={
                        <Checkbox
                          sx={{
                            '& .MuiSvgIcon-root': { fontSize: 30 },
                            color: 'inherit',
                          }}
                          onChange={() => {
                            handleFilterClick('Catégorie', c)
                          }}
                          checked={c.active}
                        />
                      }
                      label={
                        c.name === 'Street Food d’ici'
                          ? 'sur le pouce'
                          : 'restaurant'
                      }
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Box>}

            <Divider />

            <Box mb={2} pt={2}>
              <FormControl>
                <FormLabel sx={{ color: 'inherit' }} id='kitchen-type'>
                  {locales.foodTypeTitle[theme.lang]}
                </FormLabel>
                <FormGroup
                  sx={{
                    flexDirection: 'row',
                  }}
                >
                  {cuisine.map((c) => (
                    <FilterContainer key={c.name}>
                      <FormControlLabel
                        sx={{
                          span: {
                            lineHeight: '1.2',
                          },
                        }}
                        control={
                          <Checkbox
                            sx={{
                              '& .MuiSvgIcon-root': { fontSize: 30 },
                              color: 'inherit',
                            }}
                            onChange={() => {
                              handleFilterClick('Cuisine', c)
                            }}
                            checked={c.active}
                          />
                        }
                        label={c.name}
                      />
                    </FilterContainer>
                  ))}
                </FormGroup>
              </FormControl>
            </Box>

            <Divider />

            {!['bernerzeitung', 'derbund'].includes(theme.publisherDomain) &&
              <Box mb={2} pt={2}>
                <FiltersGroup>
                  <div>
                    <FormLabel sx={{ color: 'inherit' }} id='price'>
                      {locales.filtersPrice[theme.lang]}
                    </FormLabel>
                    <PriceFilter
                      min={1}
                      max={4}
                      initialValue={prix.length > 0 ? [prix[0], prix[prix.length - 1]] : [1, 4]}
                      onChange={(priceRange: number[]) => {
                        const newFilters = new Map(filters)
                        newFilters.get('Prix').forEach((a: any) => {
                          a.name >= priceRange[0] && a.name <= priceRange[1]
                            ? (a.active = true)
                            : (a.active = false)
                        })
                        setFilters(newFilters)
                      }}
                    />
                  </div>
                </FiltersGroup>
              </Box>
            }


            <Divider />

            <Footer>
              <Button
                onClick={() => {
                  const newFilters = new Map(filters)
                  newFilters.forEach((v, k) => {
                    if (k !== 'Prix') {
                      v.forEach((a: any) => {
                        a.active = false
                      })
                    }
                  })
                  setFilters(newFilters)
                }}
              >
                {locales.buttonResetFilters[theme.lang]}
              </Button>
              <Button
                color='buttonDark'
                disableElevation
                variant='contained'
                onClick={() => {
                  setShowFilters(false)
                }}
              >
                {locales.buttonApplyFilters[theme.lang]}
              </Button>
            </Footer>
          </Content>
        </Window>
      </Modal>
    </Wrapper>
  )
}

const Window = styled.div`
  border-radius: 0;
  background-color: ${(props) => props.theme.plainColor};
  text-align: left;
  box-shadow: 2px 2px 20px rgba(0, 0, 0, 0.2);
  max-width: calc(100% - 32px);
  height: 80vh;
  margin: auto;
  margin-top: 10vh;
  position: relative;

  overscroll-behavior-y: none;
  scroll-behavior: smooth;

  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    width: 100%;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    width: 640px;
  }
`

const CloseButton = styled.div`
  background-color: var(--text-color);
  margin-left: auto;
  height: 100%;
  width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 10vh;
  position: absolute;
  right: 0;
  top: 0;

  &:after {
    content: '';
    margin-top: 75%;
  }
`

const Close = styled.div`
  position: relative;
  display: inline-block;
  width: 32px;
  height: 32px;
  opacity: 0.75;

  &:before,
  &:after {
    left: 15px;
    position: absolute;
    content: ' ';
    height: 33px;
    width: 2px;
    background-color: ${(props) => props.theme.plainColor};
  }
  &:before {
    transform: rotate(45deg);
  }
  &:after {
    transform: rotate(-45deg);
  }
`

const MenuContainer = styled.div`
  height: 50px;
  font-weight: 100;
  user-select: none;
  background-color: ${(props) => props.theme.plainColor};
  border-bottom: 1px solid ${(props) => props.theme.borderColor};
  position: sticky;
  transform: translate3d(0, 0, 0);
  top: 0;
  z-index: 2;
  display: flex;
  justify-content: space-around;
  align-items: stretch;
`

const MenuInfos = styled.div`
  color: ${(props) => props.theme.textColor};
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  line-height: 1.5;
  padding-right: 2rem;
  width: calc(100% - 20vh -1em);
  float: left;
  height: 100%;
  cursor: pointer;
  font-weight: bold;
  font-size: 1.5rem;
  max-width: 600px;
  padding: 0 2rem;
  margin: auto;
  width: 100%;
`

const Footer = styled.div`
  padding: 35px 0;
  text-align: right;
`

const Content = styled.div`
  padding: 35px;
  padding-top: 0;
  color: ${(props) => props.theme.textColor};
`

const FiltersGroup = styled.div``

const PriceFilter = ({
  initialValue,
  min,
  max,
  onChange,
}: {
  initialValue: number[]
  min: number
  max: number
  onChange: (range: number[]) => void
}) => {
  const [value, setValue] = useState<number[]>(initialValue)

  const handleChange = (event: Event, newValue: number | number[]) => {
    setValue(newValue as number[])
    onChange(newValue as number[])
  }

  const marks = [
    {
      value: 1,
      label: '$',
    },
    { value: 2, label: '$$' },
    { value: 3, label: '$$$' },
    {
      value: 4,
      label: '$$$$',
    },
  ]

  return (
    <Slider
      min={min}
      max={max}
      getAriaLabel={() => 'Catégorie de prix'}
      value={value}
      onChange={handleChange}
      valueLabelDisplay='auto'
      getAriaValueText={() => 'Catégorie de prix'}
      marks={marks}
    // sx={{color: 'inherit'}}
    />
  )
}
